/** @format */

import { DOCUMENT, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { AnalyticsService, UtilityService } from '@shared/data-access/services';
import { Store } from '@ngrx/store';
import {
	refreshProductList,
	selectPageNumber,
	selectPageSize,
	selectProductList,
	selectTotalCount,
	setPageNumber
} from '@plp/data-access/state';
import { Subject } from 'rxjs';
import {
	homefieldIconChevronLeft,
	homefieldIconChevronRight,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { HomefieldTextLinkModule } from '@homefield';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';

@Component({
	selector: 'plp-components-bottom-pagination',
	imports: [
		AsyncPipe,
		HomefieldIconLibraryModule,
		HomefieldTextLinkModule,
		HmfActionTextComponent
	],
	templateUrl: './bottom-pagination.component.html',
	styleUrl: './bottom-pagination.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class BottomPaginationComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	totalPages = 0;
	displayPages: number[] = [];
	selectedIndex = 0;
	currentPageNumber = 0;
	showRightEllipsis = false;
	showLeftEllipsis = false;

	readonly pageNumber$ = this.store.select(selectPageNumber).pipe(takeUntil(this.destroy$));
	readonly productListData$ = this.store.select(selectProductList).pipe(
		takeUntil(this.destroy$),
		withLatestFrom(
			this.store.select(selectPageNumber),
			this.store.select(selectPageSize),
			this.store.select(selectTotalCount)
		),
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		tap(([productListData, pageNumber, pageSize, totalCount]) => {
			/* Page number scenarios we want to handle
			 * 	1
			 * 	1 2 | >
			 * 	1 2 3 … 20 | >
			 * 	< | 1 2
			 * 	< | 1 2 3 4 | >
			 * 	< | 1 2 3 … 20 | >
			 * 	< | 1 2 3 4 … 20 | >
			 * 	< | 1 … 3 4 5 … 20 | >
			 * 	< | 1 … 18 19 20 | >
			 * 	< | 1 … 17 18 19 20 | >
			 * 	< | 1 … 18 19 20
			 */

			this.currentPageNumber = pageNumber;

			this.totalPages = pageSize && totalCount ? Math.ceil(totalCount / pageSize) : 1;

			this.displayPages = this.calculateDisplayPages(pageNumber, this.totalPages);

			if (this.displayPages) {
				this.showRightEllipsis = !this.displayPages.includes(this.totalPages);
				this.showLeftEllipsis =
					pageNumber !== 0 && ![1, 2].some((val) => this.displayPages.includes(val));
			} else {
				this.showRightEllipsis = false;
				this.showLeftEllipsis = false;
			}
		})
	);

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private readonly utilityService: UtilityService,
		private readonly store: Store,
		private readonly analyticsService: AnalyticsService,
		private readonly homefieldIconsLibraryService: HomefieldIconsLibraryService
	) {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconChevronLeft,
			homefieldIconChevronRight
		]);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	pageNumberCountClickEvent(pageNumber: number, index?: number): void {
		this.selectedIndex = index;

		const topOfPage = this.document.getElementById('plp-srlp-content-wrapper');
		const topOfBreakoutPage = this.document.getElementById('plp-list-container');
		// scroll to top of page after page number is selected
		this.utilityService.scrollToElement(
			topOfPage || topOfBreakoutPage,
			{
				behavior: 'instant',
				block: 'start',
				inline: 'nearest'
			},
			topOfBreakoutPage ? 60 : 0
		);

		this.store.dispatch(setPageNumber({ pageNumber }));
		this.store.dispatch(refreshProductList());
		this.setAnalyticsEvent(pageNumber + 1);
	}

	lastNumberClick(totalPages: number): void {
		const index = (totalPages % 4) - 1;
		this.pageNumberCountClickEvent(totalPages - 1, index);
	}

	decreasePageNumber(): void {
		// reset the selected index if arrow click is past the displayed page numbers
		const index = this.selectedIndex <= 0 ? 3 : this.selectedIndex - 1;
		this.pageNumberCountClickEvent(this.currentPageNumber - 1, index);
	}

	increasePageNumber(): void {
		const index = this.selectedIndex >= 3 ? 0 : this.selectedIndex + 1;
		this.pageNumberCountClickEvent(this.currentPageNumber + 1, index);
	}

	/** @description update the page numbers shown in the displayed pagination */
	private calculateDisplayPages(currentPage: number, totalPages: number): number[] {
		currentPage += 1;

		// If there are less than 5 pages, then just display them all
		if (totalPages < 5) {
			return [...Array(totalPages).keys()].map((i) => i + 1);
		}

		// If we are on the 3rd page (special cases)
		if (currentPage === 3) {
			return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1];
		}

		// If we are on the 3rd page from the end (special case)
		if (currentPage === totalPages - 2) {
			return [currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
		}

		// If we are not on the end caps
		if (currentPage !== 1 && currentPage !== totalPages) {
			return [currentPage - 1, currentPage, currentPage + 1];
		}

		// If we are on the first page
		if (currentPage === 1) {
			return [currentPage, currentPage + 1, currentPage + 2];
		}

		// We must be on the last page
		return [currentPage - 2, currentPage - 1, currentPage];
	}

	private setAnalyticsEvent(pageNumber: number): void {
		const refinementUpdateObj = {
			UpdateType: 'Pagination',
			UpdateValue: pageNumber
		};
		this.analyticsService.triggerAnalyticsEvent('ResultsViewUpdate', refinementUpdateObj);
	}

	trackByFn(index: number, item: string): string {
		return index.toString() + item;
	}
}
